import mixpanel, { Dict } from "mixpanel-browser";
import urls from "global/constants/UrlConstants";
import strings from "global/constants/StringConstants";
import { IS_EMBED } from "embed/embedMode";
import {
  CREDENTIAL_BEARING_URL_REGEXES,
  isCredentialBearingUrl,
} from "utils/analyticsPaths";

/**
 * Mixpanel is PROD-only, like Clarity and PostHog. Every helper below is a
 * no-op off PROD, so callers never have to guard on `urls.PROD` themselves.
 *
 * Autocapture records page views, clicks and form submits on its own — the
 * `track*` helpers are only for events autocapture cannot infer.
 */

let initialized = false;
/** Set once init throws (ad blocker, offline) so we stop retrying it. */
let initFailed = false;

/**
 * Percentage of PROD sessions captured by session replay. Sampled rather than
 * 100% so replay is a diagnostic aid, not a standing recording of every
 * signing session. Raising this needs a privacy/DPA review first, since replay
 * data is processed by a third party.
 */
const SESSION_REPLAY_PERCENT = 10;

/** Set when we paused replay ourselves, so we only ever resume our own pause. */
let replayPausedForCredentialPath = false;

/** Drops the query string and hash, keeping origin + path. */
const stripQuery = (url: string): string => {
  if (!url) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin + parsed.pathname;
  } catch (error) {
    return url.split("#")[0].split("?")[0];
  }
};

/**
 * Overrides the URL properties the SDK derives from the browser.
 *
 * `_.info.properties()` stamps `$current_url` (window.location.href) and
 * `$referrer` (document.referrer) onto every event, query string included.
 * Super properties are merged after those defaults, so registering the same
 * keys replaces them — this is the only redaction point the SDK offers, since
 * `hooks.before_send_events` is declared in the types but implemented nowhere
 * in 2.81.0.
 *
 * Registered unpersisted so it does not accumulate in stored super properties;
 * it is re-registered on every route change.
 *
 * This is what makes query-string secrets safe generally, rather than only on
 * the paths we remembered to list. CREDENTIAL_BEARING_PATHS stays as defence
 * in depth and for session replay, which records URLs outside this mechanism.
 */
const registerSanitizedUrls = () => {
  try {
    mixpanel.register(
      {
        $current_url: stripQuery(window.location.href),
        $referrer: stripQuery(document.referrer),
      },
      { persistent: false },
    );
  } catch (error) {
    console.warn("registerSanitizedUrls: could not register URLs", error);
  }
};

/**
 * The store (and so this middleware) is built at import time, while
 * `initMixpanel` runs from App's mount effect — so a login/logout dispatched
 * during bootstrap would otherwise be dropped. Initialising on demand here
 * makes that ordering irrelevant; the call is idempotent and PROD-gated.
 */
const ensureInitialized = () => {
  if (!urls.PROD) return false;
  if (!initialized) initMixpanel();
  return initialized;
};

export const initMixpanel = () => {
  // Never inside a partner's iframe (Embedded Components): no third-party tracking in their page.
  if (!urls.PROD || initialized || initFailed || IS_EMBED) return;
  // Guarded here rather than only in App so `ensureInitialized` cannot bring
  // Mixpanel up on a credential-bearing route: a logout or 401 firing while
  // the user sits on a signing screen would otherwise start replay there.
  if (isCredentialBearingUrl(window.location.pathname, window.location.search))
    return;
  try {
    mixpanel.init(strings.MIXPANEL_TOKEN, {
      // Autocapture is a separate capture path from session replay, and none
      // of the record_* options below apply to it, so its own limits are set
      // here rather than assumed.
      autocapture: {
        // Already the default. Pinned because with it on, the text of any
        // clicked element — recipient names, document titles — would ship
        // with the event.
        capture_text_content: false,
        capture_extra_attrs: [],
        block_url_regexes: CREDENTIAL_BEARING_URL_REGEXES,
      },
      // Session replay on an e-signature product records screens holding
      // agreement text, recipient details and drawn signatures, so every
      // privacy control below is set explicitly rather than left to the
      // library default — a default that changes on upgrade must not
      // silently widen what we capture.
      record_sessions_percent: SESSION_REPLAY_PERCENT,
      record_mask_all_text: true,
      record_mask_all_inputs: true,
      // The PDF is painted to a canvas and its pdf.js text layer is real DOM
      // text; neither may leave the browser.
      record_canvas: false,
      record_block_selector: "img, video, audio, canvas, .textLayer",
      // Our own console.warn calls carry API error payloads.
      record_console: false,
    });
    initialized = true;
    registerSanitizedUrls();
  } catch (error) {
    // Latched: without this, every identify/track/reset would retry a failing
    // init and log again, since `initialized` stays false.
    initFailed = true;
    console.warn("initMixpanel: Mixpanel unavailable", error);
  }
};

/**
 * Pauses session replay while the user is on a credential-bearing route.
 *
 * `block_url_regexes` covers autocapture only. Replay is a separate recorder
 * that carries the URL in its own metadata, and the SDK exposes no per-event
 * URL redaction — stop/start is the only lever, so this has to be driven from
 * route changes rather than configured once at init.
 *
 * Resumes only a pause we caused: `start_session_recording` would otherwise
 * force-record sessions that `record_sessions_percent` never sampled.
 */
export const syncSessionReplayWithPath = (pathname: string, search: string) => {
  if (!initialized) return;
  // The registered URLs are a snapshot, so they have to be refreshed for the
  // route the user just moved to.
  registerSanitizedUrls();
  try {
    if (isCredentialBearingUrl(pathname, search)) {
      if (replayPausedForCredentialPath) return;
      const replay = mixpanel.get_session_recording_properties() as {
        $mp_replay_id?: string;
      };
      if (!replay?.$mp_replay_id) return; // this session was not sampled
      mixpanel.stop_session_recording();
      replayPausedForCredentialPath = true;
    } else if (replayPausedForCredentialPath) {
      replayPausedForCredentialPath = false;
      mixpanel.start_session_recording();
    }
  } catch (error) {
    console.warn("syncSessionReplayWithPath: could not toggle replay", error);
  }
};

export interface MixpanelUser {
  id?: string | number;
  email?: string;
  account?: string;
  name?: string;
}

export const identifyMixpanel = (user: MixpanelUser) => {
  if (!ensureInitialized()) return;
  try {
    // Keyed on the account id, which is stable — an email change would
    // otherwise fragment the person into two. Email is the fallback for the
    // paths that carry no id, and is set as `$email` below for display.
    // `||`, not `??`: callers pass a lower-cased `user.email ?? ""`, and an
    // empty string has to fall through rather than be identified as "".
    const distinctId = user.id != null ? String(user.id) : user.email || "";
    if (!distinctId) return;
    mixpanel.identify(distinctId);
    mixpanel.people.set({
      $email: user.email,
      $name: user.name,
      account: user.account,
      user_id: user.id,
    });
    if (user.account) {
      mixpanel.set_group("account", user.account);
    }
  } catch (error) {
    console.warn("identifyMixpanel: could not identify user", error);
  }
};

export const trackMixpanel = (event: string, properties?: Dict) => {
  if (!ensureInitialized()) return;
  // The SDK attaches `$current_url` to every event it sends, so a manual track
  // fired from a signing screen would carry the signer token that
  // `block_url_regexes` keeps out of the autocaptured ones.
  if (isCredentialBearingUrl(window.location.pathname, window.location.search))
    return;
  try {
    mixpanel.track(event, properties);
  } catch (error) {
    console.warn(`trackMixpanel: could not track "${event}"`, error);
  }
};

/** Clears the identified user so the next login does not inherit this session. */
export const resetMixpanel = () => {
  if (!ensureInitialized()) return;
  try {
    // The pause flag belongs to the session being discarded. Left set, the
    // next user on this browser would get `start_session_recording` on their
    // first ordinary route — recording a session sampling never picked.
    replayPausedForCredentialPath = false;
    mixpanel.reset();
  } catch (error) {
    console.warn("resetMixpanel: could not reset", error);
  }
};
